import { isEmpty } from 'lodash';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { TagOptionPill } from './TagOptionPill';
import { TagOptionsProps, getSearchPlaceholder } from './tagOptions';

type TagOption = API.TagComponentOption;

/**
 * Checkbox rows, with "Select all" first on an empty search when `selectAll` is on. Select all takes
 * every active tag, or clears them all when they're already selected.
 */
export function TagMultiSelectOptions({
  options,
  selectedValues,
  onChange,
  filter,
  setFilter,
  canCreate,
  isRequired = false,
  selectAll = true,
}: TagOptionsProps & { selectAll?: boolean }) {
  const query = filter.toLowerCase();
  const isAllSelected =
    !isEmpty(options) && options.every(option => selectedValues.includes(option.value));
  const matches = options.filter(option => (option.label ?? '').toLowerCase().includes(query));
  const rows: TagRow[] = [
    ...(selectAll && isEmpty(query) && !isEmpty(matches) ? [SELECT_ALL_ROW] : []),
    ...matches.map(option => ({ kind: 'tag' as const, option })),
  ];

  const onSelect = (row: TagRow) => {
    if (row.kind === 'all') {
      onChange(isAllSelected ? [] : options.map(option => option.value));
      return;
    }
    const { value } = row.option;
    if (!selectedValues.includes(value)) {
      onChange([...selectedValues, value]);
      return;
    }
    if (isRequired && selectedValues.length === 1) {
      return;
    }
    onChange(selectedValues.filter(item => item !== value));
  };

  return (
    <Menu.SearchableOptions
      filter={filter}
      setFilter={setFilter}
      searchPlaceholder={getSearchPlaceholder(canCreate)}
      options={rows}
      getItemKey={getTagRowKey}
      getItemLabel={getTagRowLabel}
      getItemContent={row => <TagRowContent row={row} />}
      isItemSelected={row => isTagRowSelected(row, selectedValues, isAllSelected)}
      onSelect={onSelect}
      multiple
    />
  );
}

type TagRow = { kind: 'all' } | { kind: 'tag'; option: TagOption };

const SELECT_ALL_ROW: TagRow = { kind: 'all' };

function TagRowContent({ row }: { row: TagRow }) {
  if (row.kind === 'all') {
    return (
      <Text size="body1" flex>
        {i18n('card.blocks.tagsInput.selectAllLabel')}
      </Text>
    );
  }
  return (
    <View flex marginLeft={-1} paddingVertical={0.5} paddingRight={2}>
      <TagOptionPill option={row.option} />
    </View>
  );
}

function getTagRowKey(row: TagRow) {
  if (row.kind === 'all') {
    return 'all';
  }
  return row.option.value;
}

function getTagRowLabel(row: TagRow) {
  if (row.kind === 'all') {
    return i18n('card.blocks.tagsInput.selectAllLabel');
  }
  return row.option.label ?? '';
}

function isTagRowSelected(row: TagRow, selectedValues: string[], isAllSelected: boolean) {
  if (row.kind === 'all') {
    return isAllSelected;
  }
  return selectedValues.includes(row.option.value);
}
